/**
 * Блок «Похожие модели» внизу карточки серии (владелец 22.10.26): где лежат такие же и похожие
 * платья. Вкладки — «Похожие по фото» (векторы, кэш `dress_model_similar`, кнопка «Обновить») и
 * «Связи CRM» (одинаковые, склейка, похожие). Переключатель «Все города / салон»: все города —
 * модели с плашками «город · размер×штук», клик по городу — салон; в салоне — тоже модели (не серии,
 * владелец 22.10.26): размеры салона кнопками-сериями, ниже прочие города. Отметить можно любую
 * модель (одна серия на модель: салона, иначе другого города), «В поле ответа» кладёт фото с номером
 * серии на снимке (отправляет сотрудник). Фото: колесо над карточкой листает фото модели, клик —
 * слайдер; «− +» — размер карточек; фильтр размеров — как в каталоге: размер у самой серии.
 * Наличие — правила «региона матрицы» CRM. Спека `docs/specs/2026-10-22-serial-models-design.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Minus, Plus, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { type MediaItem, MediaViewer } from "@/components/MediaViewer";
import type { OnOffer } from "@/components/chat/SerialPhotos";
import { dressModelPhotos, markDress } from "@/lib/clientChats";
import {
  LINK_LABEL,
  type ModelCard,
  type ModelSerial,
  type ModelsOf,
  type ModelsTab,
  refreshSimilar,
  type SerialModels as Data,
  serialModelsQuery,
  stampSerial,
} from "@/lib/serialModels";

const MAX_PICK = 10;
const pill = "rounded-full px-1.5 py-px text-[11px] font-medium leading-[16px]";
const head = "text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted";
const tabCls = (on: boolean) =>
  `-mb-px border-b-2 px-2 pb-1 text-[12.5px] ${on ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"}`;
// Ширина карточки «− +», запоминаем в браузере сотрудника.
const WIDTHS = [120, 150, 190, 240, 300];
const WIDTH_KEY = "prokat.similar.width";
const WHEEL_STEP = 60; // тачпад шлёт мелкие шаги — копим до «щелчка» колеса

type Serial = ModelSerial & { city?: string };
type Pick = { serial: number; model_id: number; photo: string; size: string; city: string | null };

function sizes(c: ModelCard["cities"][number]) {
  return c.sizes.map((s) => (s.n > 1 ? `${s.size}×${s.n}` : s.size)).join(" · ");
}

/** Размеры по возрастанию, «БР» последним — как `stock._порядок_размера`. */
function bySize(a: string, b: string) {
  const n = (v: string) => (v === "БР" ? Infinity : Number(v.replace(",", ".")));
  const x = n(a);
  const y = n(b);
  return Number.isNaN(x) || Number.isNaN(y) ? a.localeCompare(b) : x - y;
}

const photosQuery = (model: number) => ({
  queryKey: ["dress-model-photos", model],
  queryFn: () => dressModelPhotos(model),
  staleTime: Infinity,
  retry: false,
});

/** Фото модели: колесо над фото листает фото модели из индекса (грузятся при наведении), на краях
 *  отдаёт прокрутку списку; клик — слайдер с того же фото; кружок справа сверху — отметка. */
function Photo({
  m,
  mine,
  canPick,
  onPick,
  onOpen,
}: {
  m: ModelCard;
  mine: boolean;
  canPick: boolean;
  onPick: () => void;
  onOpen: (at: number) => void;
}) {
  const [hover, setHover] = useState(false);
  const [at, setAt] = useState(0);
  const q = useQuery({ ...photosQuery(m.model_id), enabled: hover });
  const list = q.data?.photos.length ? q.data.photos.map((p) => p.photo_url) : m.photo_url ? [m.photo_url] : [];
  const box = useRef<HTMLDivElement>(null);
  const state = useRef({ n: list.length, at, acc: 0 });
  state.current.n = list.length;
  state.current.at = at;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const s = state.current;
      const dir = Math.sign(e.deltaY);
      if (s.n < 2 || dir === 0 || (dir > 0 && s.at >= s.n - 1) || (dir < 0 && s.at <= 0)) return;
      e.preventDefault();
      s.acc += e.deltaY;
      if (Math.abs(s.acc) < WHEEL_STEP) return;
      s.acc = 0;
      setAt((x) => Math.min(Math.max(x + dir, 0), s.n - 1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const src = list[Math.min(at, list.length - 1)];
  return (
    <div
      ref={box}
      className="group relative"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setAt(0);
        state.current.acc = 0;
      }}
    >
      {src ? (
        <button type="button" onClick={() => onOpen(at)} className="block w-full" title="Открыть фото · колесо — листать фото модели">
          <img src={src} alt="" loading="lazy" className="aspect-[3/4] w-full rounded-[var(--radius-ui)] bg-black/5 object-cover" />
        </button>
      ) : (
        <div className="grid aspect-[3/4] w-full place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">нет фото</div>
      )}
      {list.length > 1 && (
        <div className="pointer-events-none absolute inset-x-1 bottom-1 flex justify-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {list.length <= 12 ? (
            list.map((_, i) => <span key={i} className={`h-1 flex-1 rounded-full ${i === at ? "bg-white" : "bg-white/45"}`} style={{ maxWidth: 18 }} />)
          ) : (
            <span className="rounded-full bg-black/55 px-1.5 text-[10.5px] tabular-nums text-white">
              {at + 1} / {list.length}
            </span>
          )}
        </div>
      )}
      {canPick && (
        <button
          type="button"
          onClick={onPick}
          title={mine ? "Снять отметку" : "Отметить модель: серия без брони"}
          className={`absolute right-1 top-1 grid size-6 place-items-center rounded-full border-2 ${mine ? "border-accent bg-accent text-white" : "border-white bg-black/30 hover:bg-black/50"}`}
        >
          {mine && <Check size={14} />}
        </button>
      )}
    </div>
  );
}

/** Слайдер фото модели; кнопка в шапке — отметить или снять модель. */
function Viewer({
  m,
  start,
  mine,
  canPick,
  onPick,
  onClose,
}: {
  m: ModelCard;
  start: number;
  mine: Pick | undefined;
  canPick: boolean;
  onPick: () => void;
  onClose: () => void;
}) {
  const q = useQuery(photosQuery(m.model_id));
  if (q.isPending) return null;
  const urls = q.data?.photos.length ? q.data.photos.map((p) => p.photo_url) : m.photo_url ? [m.photo_url] : [];
  const items: MediaItem[] = urls.map((url) => ({ kind: "photo", url, caption: m.name, meta: mine ? `серия ${mine.serial} · р. ${mine.size}` : null }));
  if (items.length === 0) return null;
  return (
    <MediaViewer
      items={items}
      start={start}
      onClose={onClose}
      actions={() =>
        canPick && (
          <button
            type="button"
            onClick={onPick}
            className={`inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[13px] ${mine ? "bg-white font-medium text-black hover:bg-white/90" : "bg-white/10 hover:bg-white/20"}`}
          >
            <Check size={15} aria-hidden /> {mine ? "Отмечено" : "Отметить"}
          </button>
        )
      }
    />
  );
}

function Caption({ m, sizes: list }: { m: ModelCard; sizes?: string[] }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 text-[11.5px] leading-[15px]">
      <span className="font-medium text-fg">{m.name}</span>
      {list && list.length > 0 && <span className="tabular-nums text-fg-muted">р. {list.join(", ")}</span>}
      {m.link !== "photo" && <span className="text-fg-muted">{LINK_LABEL[m.link]}</span>}
    </div>
  );
}

/** Города модели плашками «город · размер×штук»; клик — этот салон. `skip` — уже выбранный салон:
 *  тогда остальные города коротко «город штук», размеры — в подсказке. */
function Cities({ m, onCity, skip = null }: { m: ModelCard; onCity: (id: number) => void; skip?: number | null }) {
  const list = m.cities.filter((c) => c.id !== skip);
  if (list.length === 0) return null;
  if (skip != null)
    return (
      <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 text-[11px] leading-[15px] text-fg-muted">
        <span>ещё:</span>
        {list.map((c) => (
          <button key={c.id} type="button" onClick={() => onCity(c.id)} className="hover:text-fg hover:underline" title={`${c.short}: ${sizes(c)}`}>
            {c.short} <span className="tabular-nums">{c.total}</span>
          </button>
        ))}
      </div>
    );
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onCity(c.id)}
          className="rounded-[6px] border border-border px-1 text-left text-[11px] leading-[15px] hover:bg-bg-subtle"
          title={`Свободные серии модели в ${c.short}${c.transit ? `, в транзите ${c.transit}` : ""}`}
        >
          <span className="font-medium">{c.short}</span> · {sizes(c)}
          {c.transit > 0 && <span className="text-warn"> · транзит {c.transit}</span>}
        </button>
      ))}
    </div>
  );
}

function readWidth(wide: boolean) {
  try {
    const v = Number(localStorage.getItem(WIDTH_KEY));
    if (WIDTHS.includes(v)) return v;
  } catch {
    /* без хранилища — по умолчанию */
  }
  return wide ? WIDTHS[2]! : WIDTHS[1]!;
}

export function SerialModels({
  of,
  start = null,
  title = "Похожие модели",
  wide = false,
  onOffer,
}: {
  of: ModelsOf;
  /** Салон сразу: у модели без серии сервер города не знает — «Товар» даёт город чата. */
  start?: number | null | undefined;
  title?: string;
  /** Большое окно: карточки крупнее. */
  wide?: boolean;
  onOffer?: OnOffer | undefined;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<ModelsTab>("similar");
  // null — «как решит сервер» (город серии); 0 — все города; иначе — салон.
  const [location, setLocation] = useState<number | null>(start);
  const [picked, setPicked] = useState<Pick[]>([]);
  const [want, setWant] = useState<string[]>([]);
  const [width, setWidth] = useState(() => readWidth(wide));
  const [open, setOpen] = useState<{ m: ModelCard; at: number } | null>(null);
  const q = useQuery(serialModelsQuery(of, tab, location || null));
  const d = q.data;
  const city = location === 0 ? null : (location ?? d?.location_id ?? null);
  // Серии салона приходят только с `location`: город серии сервер выбрал сам — карточки серий берём из того же ответа.
  const refresh = useMutation({
    mutationFn: () => refreshSimilar(of, location || null),
    onSuccess: (fresh: Data) => qc.setQueryData(serialModelsQuery(of, "similar", location || null).queryKey, fresh),
  });
  const offer = useMutation({
    mutationFn: async () => {
      const files = await Promise.all(picked.map((p) => stampSerial(p.photo, p.serial)));
      files.forEach((f, i) => markDress(f, { origin: "serial", model_id: picked[i]!.model_id }));
      return files;
    },
    onSuccess: (files) => {
      onOffer?.("", files);
      setPicked([]);
    },
  });
  const choose = (id: number) => {
    setLocation(id);
    setPicked([]);
  };
  const zoom = (dir: number) => {
    const next = WIDTHS[Math.min(Math.max(WIDTHS.indexOf(width) + dir, 0), WIDTHS.length - 1)]!;
    setWidth(next);
    try {
      localStorage.setItem(WIDTH_KEY, String(next));
    } catch {
      /* не запомним — не беда */
    }
  };

  const fits = (s: Serial) => want.length === 0 || want.includes(s.size);
  // У модели одна отметка: фото — обложка модели, размер — эта серия вместо прежней.
  const pickSerial = (m: ModelCard, s: Serial) =>
    setPicked((had) => {
      if (had.some((x) => x.serial === s.serial)) return had.filter((x) => x.serial !== s.serial);
      const rest = had.filter((x) => x.model_id !== m.model_id);
      const pick = { serial: s.serial, model_id: m.model_id, photo: m.photo_url ?? "", size: s.size, city: s.city ?? null };
      return rest.length < MAX_PICK ? [...rest, pick] : had;
    });
  // Серия к отметке: салона, иначе другого города; под фильтр размеров; без брони и транзита — раньше.
  const pickModel = (m: ModelCard) => {
    if (picked.some((x) => x.model_id === m.model_id)) return setPicked((p) => p.filter((x) => x.model_id !== m.model_id));
    const pool = [...m.serials.filter(fits), ...m.others.filter(fits)];
    const best = pool.find((s) => !s.next_deal && !s.transit) ?? pool[0] ?? m.serials[0] ?? m.others[0];
    if (best) pickSerial(m, best);
  };

  const cards = d?.models ?? [];
  const allSizes = [...new Set(cards.flatMap((m) => [...m.serials, ...m.others].map((s) => s.size)))].sort(bySize);
  const here = city == null ? [] : cards.filter((m) => m.serials.some(fits));
  const elsewhere = cards.filter((m) => !here.includes(m) && m.others.some(fits));
  const cityName = d?.locations.find((l) => l.id === city)?.short;
  const canPick = (m: ModelCard) => !!onOffer && !!m.photo_url && m.serials.length + m.others.length > 0;
  const grid = { display: "grid", gap: 8, gridTemplateColumns: `repeat(auto-fill, minmax(${width}px, 1fr))` };
  const flip = (size: string) => setWant((w) => (w.includes(size) ? w.filter((x) => x !== size) : [...w, size]));

  const mineOf = (m: ModelCard) => picked.find((x) => x.model_id === m.model_id);
  const photo = (m: ModelCard) => (
    <Photo m={m} mine={!!mineOf(m)} canPick={canPick(m)} onPick={() => pickModel(m)} onOpen={(at) => setOpen({ m, at })} />
  );
  const pickedLine = (p: Pick | undefined) =>
    p && (
      <div className="text-[11px] tabular-nums text-fg-muted">
        серия {p.serial} · р. {p.size}
        {p.city ? ` · ${p.city}` : ""}
      </div>
    );
  // Карточка модели вне салона: размеры под фильтр — в подписи, города — плашками.
  const other = (m: ModelCard, dim: boolean) => {
    const mine = mineOf(m);
    return (
      <div
        key={m.model_id}
        className={`flex flex-col gap-1 rounded-[var(--radius-ui)] p-1 ${mine ? "bg-accent/15 ring-2 ring-accent" : dim ? "opacity-85" : ""}`}
        data-annot={`модель ${m.model_id}`}
      >
        {photo(m)}
        <Caption m={m} sizes={[...new Set(m.others.filter(fits).map((s) => s.size))].sort(bySize)} />
        {pickedLine(mine)}
        <Cities m={m} onCity={choose} />
      </div>
    );
  };

  return (
    <section className="flex flex-col gap-2 border-t border-border py-3" data-annot="похожие модели серии">
      <div className="flex items-center gap-2">
        <div className={head}>{title}</div>
        <div className="ml-auto flex items-center rounded-full border border-border" data-annot="размер карточек">
          <button
            type="button"
            onClick={() => zoom(-1)}
            disabled={width === WIDTHS[0]}
            className="grid size-6 place-items-center rounded-full text-fg-muted hover:bg-bg-subtle hover:text-fg disabled:opacity-40"
            title="Карточки меньше"
          >
            <Minus size={13} />
          </button>
          <button
            type="button"
            onClick={() => zoom(1)}
            disabled={width === WIDTHS[WIDTHS.length - 1]}
            className="grid size-6 place-items-center rounded-full text-fg-muted hover:bg-bg-subtle hover:text-fg disabled:opacity-40"
            title="Карточки крупнее"
          >
            <Plus size={13} />
          </button>
        </div>
        <div className="flex gap-1 border-b border-border">
          <button type="button" className={tabCls(tab === "similar")} onClick={() => setTab("similar")}>
            По фото
          </button>
          <button type="button" className={tabCls(tab === "models")} onClick={() => setTab("models")}>
            Связи CRM
          </button>
        </div>
      </div>
      {d && (
        <div className="flex flex-wrap items-center gap-1" data-annot="город похожих">
          <button
            type="button"
            onClick={() => choose(0)}
            className={`${pill} ${city == null ? "bg-accent text-white" : "border border-border hover:bg-bg-subtle"}`}
          >
            Все города
          </button>
          {d.locations.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => choose(l.id)}
              className={`${pill} ${city === l.id ? "bg-accent text-white" : "border border-border hover:bg-bg-subtle"}`}
            >
              {l.short}
            </button>
          ))}
        </div>
      )}
      {allSizes.length > 1 && (
        <div className="flex flex-wrap items-center gap-1" data-annot="размер похожих">
          <span className="mr-0.5 text-[11.5px] text-fg-muted">Размер:</span>
          {allSizes.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => flip(s)}
              className={`${pill} tabular-nums ${want.includes(s) ? "bg-accent text-white" : "border border-border hover:bg-bg-subtle"}`}
            >
              {s}
            </button>
          ))}
          {want.length > 0 && (
            <button type="button" onClick={() => setWant([])} className="ml-1 text-[11.5px] text-fg-muted hover:underline">
              все размеры
            </button>
          )}
        </div>
      )}
      {tab === "similar" && d && (
        <div className="flex items-center gap-2 text-[11.5px] text-fg-muted">
          <span>
            {d.computed_date ? `посчитано ${d.computed_date.slice(8, 10)}.${d.computed_date.slice(5, 7)} ${d.computed_date.slice(11, 16)}` : "ещё не считали"}
            {d.error ? ` · ${d.error}` : ""}
          </span>
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-accent-text hover:bg-bg-subtle disabled:opacity-60"
            title="Пересчитать похожих этой модели по векторам фото сейчас"
          >
            <RefreshCw size={12} className={refresh.isPending ? "animate-spin" : ""} /> Обновить
          </button>
        </div>
      )}
      {q.isPending ? (
        <div className="text-[12.5px] text-fg-muted">{tab === "similar" ? "Ищу похожие по фото…" : "Загружаю связи из CRM…"}</div>
      ) : q.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить похожие: {q.error.message}</div>
      ) : city == null ? (
        elsewhere.length === 0 ? (
          <div className="text-[12.5px] text-fg-muted">{want.length ? `Размера ${want.join(", ")} нет ни в одном городе.` : "Свободных серий нет."}</div>
        ) : (
          <div style={grid}>{elsewhere.map((m) => other(m, false))}</div>
        )
      ) : (
        <>
          {here.length === 0 ? (
            <div className="text-[12.5px] text-fg-muted">
              В {cityName ?? "салоне"} свободных серий {want.length ? `размера ${want.join(", ")} ` : ""}у этих моделей нет.
            </div>
          ) : (
            <div className="text-[11px] text-fg-faint">
              Размер — свободная серия в {cityName}, клик отмечает её (номер серии — в подсказке);{" "}
              <span className="text-warn">рыжий</span> — есть бронь, пунктир — в транзите.
            </div>
          )}
          <div style={grid}>
            {here.map((m) => {
              const mine = mineOf(m);
              return (
                <div
                  key={m.model_id}
                  className={`flex flex-col gap-1 rounded-[var(--radius-ui)] p-1 ${mine ? "bg-accent/15 ring-2 ring-accent" : ""}`}
                  data-annot={`модель ${m.model_id}`}
                >
                  {photo(m)}
                  <Caption m={m} sizes={[...new Set(m.serials.filter(fits).map((s) => s.size))].sort(bySize)} />
                  <div className="text-[11px] leading-[15px] text-fg-muted">
                    <span className="font-medium text-fg">{cityName}</span> · {m.serials.length} шт.
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {m.serials.filter(fits).map((s) => (
                      <button
                        key={s.serial}
                        type="button"
                        disabled={!canPick(m)}
                        onClick={() => pickSerial(m, s)}
                        title={`Серия ${s.serial}${s.place ? ` · ${s.place}` : ""}${s.next_deal ? ` · бронь, сделка ${s.next_deal}` : ""}${s.transit ? " · в транзите" : ""}`}
                        className={`rounded-[6px] border px-1.5 text-[11px] leading-[17px] tabular-nums ${
                          mine?.serial === s.serial
                            ? "border-accent bg-accent text-white"
                            : s.next_deal
                              ? "border-warn/60 text-warn hover:bg-warn/10"
                              : "border-border hover:bg-bg-subtle"
                        } ${s.transit ? "border-dashed" : ""}`}
                      >
                        {s.size}
                      </button>
                    ))}
                  </div>
                  {pickedLine(mine)}
                  <Cities m={m} onCity={choose} skip={city} />
                </div>
              );
            })}
          </div>
          {elsewhere.length > 0 && (
            <>
              <div className="pt-1 text-[11.5px] text-fg-muted">
                Нет в {cityName ?? "салоне"}
                {want.length ? ` размера ${want.join(", ")}` : ""}, есть в других городах:
              </div>
              <div style={grid}>{elsewhere.map((m) => other(m, true))}</div>
            </>
          )}
        </>
      )}
      {d && d.hidden > 0 && <div className="text-[11.5px] text-fg-faint">ещё {d.hidden} без свободных серий</div>}
      {onOffer && picked.length > 0 && (
        <div className="sticky bottom-0 flex items-center gap-2 border-t border-border bg-bg py-2">
          <span className="text-[12.5px]">
            Отмечено {picked.length} из {MAX_PICK}
          </span>
          <button type="button" onClick={() => setPicked([])} className="text-[12px] text-fg-muted hover:underline">
            сбросить
          </button>
          <button
            type="button"
            onClick={() => offer.mutate()}
            disabled={offer.isPending}
            className="ml-auto rounded-full bg-accent px-3 py-1 text-[12.5px] font-medium text-white disabled:opacity-60"
          >
            {offer.isPending ? "Готовлю фото…" : "В поле ответа"}
          </button>
          {offer.isError && <span className="text-[12px] text-err">{offer.error.message}</span>}
        </div>
      )}
      {open && (
        <Viewer
          m={open.m}
          start={open.at}
          mine={mineOf(open.m)}
          canPick={canPick(open.m)}
          onPick={() => pickModel(open.m)}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}
